/**
 * 兌換報表 — **代理商** realm（`/api/admin/reseller/redeem-reports/*`；批107 項5）。
 *
 * 寫法比照 `api/reseller.ts`：**獨立前綴、獨立一個檔**，代理商的頁面只 import 這個檔，
 * 不會不小心呼到 `api/redeemReports.ts` 裡那些對他一律 403 的東西。
 *
 * 三件後端決定（不是前端藏起來）的差別：
 *
 * 1. **沒有 `reseller_id` 參數**——視角由 token 決定，所以下面的函式簽章連收都不收。
 * 2. `stats.resellers` 恆為 `null`（代理商排行伺服器端就不計算）。
 * 3. 明細沒有 `client_ip`／`reseller_name`／我方員工 email；改帶 `issued_by_label`／
 *    `voided_by_label`（我方代發顯示「我方代發」、我方作廢顯示「我方」）。
 *
 * 型別與我方版**共用同一組**（從 `api/redeemReports.ts` 匯入並原樣 re-export）——
 * 兩邊各寫一份的下場就是某一天我方那份加了一欄、這份沒加。
 */
import { api } from './client'
import type {
  IssuedQuery,
  IssuedRow,
  RedeemStats,
  RedemptionQuery,
  RedemptionRow,
  ReportPage,
  ReportQuery,
} from './redeemReports'
import { reportQs } from './redeemReports'

export type {
  IssuedQuery,
  IssuedRow,
  RedeemStats,
  RedemptionQuery,
  RedemptionRow,
  ReportPage,
  ReportQuery,
}

const P = '/api/admin/reseller/redeem-reports'

/**
 * 丟掉 `reseller_id`——**在送出之前就拿掉**，不是靠後端忽略。
 *
 * 後端確實沒有宣告那個參數（FastAPI 會直接忽略未宣告的 query），但讓它出現在網路請求裡
 * 會讓讀 devtools 的人以為代理商也能指定別家，是很糟的誤導。
 */
function strip<T extends ReportQuery>(q: T): Omit<T, 'reseller_id'> {
  const { reseller_id: _drop, ...rest } = q
  return rest
}

export const reportStats = (q: ReportQuery = {}) =>
  api<RedeemStats>(`${P}/stats${reportQs(strip(q) as Record<string, string | undefined>)}`)

export const reportIssued = (q: IssuedQuery = {}) =>
  api<ReportPage<IssuedRow>>(
    `${P}/issued${reportQs(strip(q) as Record<string, string | undefined>)}`,
  )

export const reportRedemptions = (q: RedemptionQuery = {}) =>
  api<ReportPage<RedemptionRow>>(
    `${P}/redemptions${reportQs(strip(q) as Record<string, string | undefined>)}`,
  )

/** CSV 匯出（原始 `Response`；後端記稽核、可能帶 `X-Export-Truncated`）。 */
export const reportIssuedCsv = (q: IssuedQuery = {}) =>
  api<Response>(
    `${P}/issued${reportQs(strip(q) as Record<string, string | undefined>, { format: 'csv' })}`,
    { raw: true },
  )

export const reportRedemptionsCsv = (q: RedemptionQuery = {}) =>
  api<Response>(
    `${P}/redemptions${reportQs(strip(q) as Record<string, string | undefined>, {
      format: 'csv',
    })}`,
    { raw: true },
  )
